import PropTypes from 'prop-types';
import React, { useReducer, useMemo, useEffect, useCallback } from 'react';
import Modal from '@strava/ui/Modal';
import { useHistory } from 'react-router-dom';
import { trackV2 } from 'utils/analytics';
import { isDev, devLog } from 'utils/dev-utils';
import { isNil } from 'lodash-es';

import { isMobile } from 'utils/general-utils';
import { reducer } from './state/Onboarding.reducer';
import {
  CATEGORIES,
  EXISTING_ATHLETE_FLOW,
  NEW_ATHLETE_FLOW
} from './shared/onboardingConstants';
import { OnboardingContextProvider } from './state/OnboardingContext';
import { stepToUrl } from './shared/onboardingUtils';
import styles from './Onboarding.scss';
import {
  UPDATE_STEP,
  UPDATE_SUGGESTED_FRIENDS
} from './state/Onboarding.actions';
import { getSuggestedFollows } from '../../api/athlete';
import NewAthleteFlowRoutes from './components/routes/NewAthleteFlowRoutes';
import ExistingAthleteFlowRoutes from './components/routes/ExistingAthleteFlowRoutes';
import { parseServerDateString } from '../../utils/general-utils';

const Onboarding = ({
  category,
  currentAthlete,
  step,
  fbAppId,
  fbVersion,
  locale
}) => {
  const history = useHistory();

  const currentStep =
    category === CATEGORIES.newAthlete
      ? NEW_ATHLETE_FLOW.createProfile
      : EXISTING_ATHLETE_FLOW.welcome;

  const dob = (() => {
    if (currentAthlete.dob) {
      return parseServerDateString(currentAthlete.dob);
    }
    return {};
  })();

  const [state, dispatch] = useReducer(reducer, {
    currentAthlete: {
      ...currentAthlete,
      dob
    },
    currentStep,
    fbAppId,
    fbVersion,
    locale,
    category,
    alert: null,
    suggestions: []
  });

  const track = useCallback(
    (fields = {}) => {
      trackV2({
        category:
          category === CATEGORIES.newAthlete ? 'onboarding' : 'consents',
        page: state.currentStep,
        ...fields,
        properties: {
          is_mobile: isMobile(),
          ...fields.properties
        }
      });
    },
    [category, state.currentStep]
  );

  const providerState = useMemo(
    () => ({
      state,
      dispatch,
      track
    }),
    [state, track]
  );

  /**
   * Updates the current step in the state when
   * an athlete navigates to a specific step on the
   * existing user - consents flow.
   *
   * Note - new user reg flow does not currently support navigating to
   * a specific step.
   * See README#local-development for more details
   */
  useEffect(() => {
    if (category === CATEGORIES.existingAthlete) {
      dispatch({
        type: UPDATE_STEP,
        payload: { nextStep: step }
      });
    }
    devLog(`[Onboarding] routing to path - ${window.location.pathname}`);
  }, [category, step]);

  // For local development only
  // See README#local-development for more details
  useEffect(() => {
    if (!isNil(step) && isDev() && category === CATEGORIES.newAthlete) {
      // suggested friends step gets suggestions from complete profile response,
      // so we need to grab that data directly when skipping straight to `?step=suggested_friends`
      if (step === NEW_ATHLETE_FLOW.suggestedFriends) {
        (async () => {
          const response = await getSuggestedFollows({
            callsite: 'onboarding'
          });
          if (response.suggestions.length > 0) {
            dispatch({
              type: UPDATE_SUGGESTED_FRIENDS,
              payload: {
                suggestions: response.suggestions
              }
            });
          }
        })();
      }

      const path = stepToUrl({ category, step });

      dispatch({
        type: UPDATE_STEP,
        payload: { nextStep: step }
      });

      history.push(path);
      devLog(`[Onboarding - DEV] routing to path - ${path}`);
    }
  }, [history, category, step]);

  useEffect(() => {
    window.onpopstate = () => {
      const newLocation = window.location.href;
      const nextStep = newLocation
        .substring(newLocation.lastIndexOf('/') + 1)
        .replace('-', '_'); // need to match step constants, which use snake_case
      devLog(`[Onboarding] back_forward traversal - ${nextStep}`);
      dispatch({
        type: UPDATE_STEP,
        payload: { nextStep }
      });
    };
  });

  return (
    <OnboardingContextProvider value={providerState}>
      <Modal
        className={styles.modal}
        aria-labelledby="onboarding-step-title"
        isOpen={true}
      >
        {category === CATEGORIES.newAthlete && <NewAthleteFlowRoutes />}
        {category === CATEGORIES.existingAthlete && (
          <ExistingAthleteFlowRoutes />
        )}
      </Modal>
    </OnboardingContextProvider>
  );
};

Onboarding.defaultProps = {
  step: null
};

Onboarding.propTypes = {
  category: PropTypes.oneOf(Object.keys(CATEGORIES)).isRequired,
  currentAthlete: PropTypes.shape({
    id: PropTypes.number,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    dob: PropTypes.string,
    gender: PropTypes.string
  }).isRequired,
  step: PropTypes.string,
  fbAppId: PropTypes.number.isRequired,
  fbVersion: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired
};

export default Onboarding;
